import { ScaffoldDropZone } from "../../../scaffold";
import { reduceDataPath } from "../../../utility";
import { isOperation } from "../../typeGuards";
import {
  ArsTableCellData,
  ArsTableCellType,
  ArsTableSidebarItemKind,
  ArsTableState,
  ARSTActionDropAction,
  BOTTOM_EDGE_ZONES,
  RIGHT_EDGE_ZONES,
} from "../interface";
import { arst_addOperationsToGroup } from "./arst_addOperationsToGroup";
import { arst_operationToTableGroup } from "./arst_arsToTableGroup";

/**
 * Inserts dropped operations before or after an existing operation cell.
 *
 * @param state - Current ARS table state
 * @param action - Drop action describing the target operation position
 * @returns Updated ARS table state
 */
export const arst_insertOperationToGroup = <T>(
  state: ArsTableState<T>,
  action: ARSTActionDropAction,
): ArsTableState<T> => {
  const targetCell: ArsTableCellData<T> | undefined =
    state.scaffoldModel?.cellData[action.rowId]?.[action.columnId];
  if (!targetCell) return state;

  const parentDataPath = reduceDataPath(targetCell.dataPath, 2);
  const pathParts = targetCell.dataPath.split("/").filter(Boolean);
  const operationIndex = parseInt(pathParts[pathParts.length - 1], 10);
  const rowOperationAfterZones = new Set<ScaffoldDropZone>([
    ...BOTTOM_EDGE_ZONES,
    ScaffoldDropZone.BottomLeft,
    ScaffoldDropZone.BottomRight,
  ]);
  const isAfterZone =
    targetCell.cellType === ArsTableCellType.RowOperation
      ? rowOperationAfterZones.has(action.zone)
      : RIGHT_EDGE_ZONES.has(action.zone);
  const insertAt = isAfterZone ? operationIndex + 1 : operationIndex;
  const newOperations = action.payload
    .flatMap((item) =>
      item.kind === ArsTableSidebarItemKind.AnalysisMethod
        ? item.dropItem.operations
        : [item.dropItem],
    )
    .filter(isOperation)
    .map((operation) => arst_operationToTableGroup(operation, state.settings.editorProps));
  const newBodyData = arst_addOperationsToGroup(
    state.bodyData,
    parentDataPath,
    newOperations,
    state.settings.editorProps.isT,
    insertAt,
  );
  if (!newBodyData) return state;

  return {
    ...state,
    bodyData: newBodyData,
    changed: true,
  };
};
